import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {catchError, from, Observable, switchMap, throwError} from "rxjs";
import {TenantService} from "../tenant/tenant.service";
import {OAuthService} from "angular-oauth2-oidc";
import {AuthConfigService} from "./auth-config.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    constructor(
        private tenantService: TenantService,
        private authService: OAuthService,
        private authConfigService: AuthConfigService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const authConfig = this.authConfigService.getConfig();

        // E2E mode: use IgAuthorization header instead of OAuth2
        if (authConfig.e2e) {
            return this.handleE2eRequest(req, next, authConfig.tenant_alias);
        }

        // any call to the gateway must be processed without any token or tenant information
        if (req.url.startsWith(authConfig.gateway)) {
            return next.handle(req);
        }

        // any call to the authorization server should be without the access token!
        if (req.url.startsWith(authConfig.oauth2_issuer!)) {
            return next.handle(req);
        }


        if (!req.url.includes("/mvseureg/eu/") && !req.url.startsWith("https://graph.microsoft.com")) {

            const tenantInfo = this.tenantService.getTenantInfo();

            if (!tenantInfo) {
                console.error("Tenant information not provided within interceptor!");
            }

            // retrieve access token
            const accessToken = this.authService.getAccessToken();

            if (!accessToken) {
                console.error("accessToken missing!");
            }

            const tenantRequest = this.addHeaders(req, tenantInfo!.tenant, accessToken);

            return next.handle(tenantRequest).pipe(
                catchError((error: HttpErrorResponse) => {

                    if (error.status === 401  && this.authService.getRefreshToken()) {
                        return this.handleTokenRefresh(req, next, tenantInfo!.tenant, error);
                    }

                    return throwError(() => error);
                })
            );


        } else {
            return next.handle(req);
        }
    }

    /**
     * E2E auth is NOT OAuth2 — the backend authenticates these calls with IgAuthenticationFilter,
     * which reads the "IgAuthorization" header and nothing else. For an ACCESS token the only
     * valid prefix there is "Bearer ig_access " (SecurityConstants.TOKEN_PREFIX_IG_ACCESS); the
     * filter additionally knows "Bearer ig_pass " for pass codes, which is not what we send here.
     *
     * A plain "Authorization" header is not merely useless here, it actively breaks the request —
     * but NOT by pre-empting the Ig filter, so do not try to "fix" this by reordering filters.
     * The Ig filter runs FIRST (SecurityConfig registers it via addFilterBefore
     * UsernamePasswordAuthenticationFilter, which Spring orders ahead of
     * BearerTokenAuthenticationFilter) and it authenticates successfully. The damage happens
     * afterwards: BearerTokenAuthenticationFilter does not check whether an Authentication is
     * already present, cannot validate the IG token against the OAuth2 issuer, DISCARDS the Ig
     * authentication that was already in the security context, and answers 401.
     *
     * Measured against the running backend (tenantA): IgAuthorization alone => 200, Authorization
     * alone => 401, BOTH headers together => 401. This branch must therefore send IgAuthorization
     * and must NOT send Authorization.
     */
    private handleE2eRequest(req: HttpRequest<any>, next: HttpHandler, tenant: string): Observable<HttpEvent<any>> {
        // localStorage keeps precedence: the E2E runners write it and must stay in control.
        // The configured token is only a fallback for environments a human opens in a browser.
        const e2eToken = localStorage.getItem('e2e_access_token')
            || this.authConfigService.getConfig().e2e_access_token
            || '';
        // Must send IgAuthorization with the ig_access prefix here, per the contract documented in
        // environment.local-tenanta-e2e.ts and the root CLAUDE.md. Sending "Authorization" alongside
        // it does NOT work - the backend validates Authorization as an OAuth2 JWT and rejects the
        // request before ever reading IgAuthorization, so the two are mutually exclusive. "Authorization"
        // was introduced here by 70e31e457 (an unrelated CT-module commit) and silently broke this path.
        const e2eRequest = req.clone({
            setHeaders: {
                "x-alpha-tenant": tenant,
                "IgAuthorization": `Bearer ig_access ${e2eToken}`
            }
        });
        return next.handle(e2eRequest);
    }

    private addHeaders(req: HttpRequest<any>, tenant: string, token: string): HttpRequest<any> {
        return req.clone({
            setHeaders: {
                "x-alpha-tenant": tenant,
                "Authorization": `Bearer ${token}`
            }
        });
    }

    private handleTokenRefresh(
        originalReq: HttpRequest<any>,
        next: HttpHandler,
        tenant: string,
        originalError: any
    ): Observable<HttpEvent<any>> {

        return from(this.authService.refreshToken()).pipe(
            switchMap(() => {

                const newToken = this.authService.getAccessToken();

                const retryReq = this.addHeaders(originalReq, tenant, newToken);

                return next.handle(retryReq);
            }),
            catchError(refreshErr => {
                console.error("Token refresh failed. Logging out.", refreshErr);
                this.authService.logOut();
                return throwError(() => originalError);
            })
        );
    }

}
